"use client";

import { useMemo } from "react";
import { useFeatureFlags } from "@/lib/contexts/feature-flags-context";
import { useCRMRoleCheck, useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useNavigationCustomization } from "@/lib/hooks/settings/use-navigation-customization";
import { buildNavigationSections } from "../navigation-builder";
import type { NavigationConfig } from "../types";

export function useQuickActionsNavigation() {
  const { isFeatureEnabled } = useFeatureFlags();
  const { canAccessCRM } = useCRMRoleCheck();
  const roleCheck = useRoleCheck();

  // Navigation customization setup
  const defaultSections = useMemo(() => {
    const config: NavigationConfig = {
      userType: roleCheck.roleName || "",
      isAdmin: roleCheck.isAdmin,
      sections: [], // This will be populated by buildNavigationSections
      featureFlags: {
        canViewDashboard: isFeatureEnabled("canViewDashboard"),
        canViewTickets: isFeatureEnabled("canViewTickets"),
        canViewInternalTickets: isFeatureEnabled("canViewInternalTickets"),
        canCreateTickets: isFeatureEnabled("canCreateTickets"),
        canUseTicketTemplates: isFeatureEnabled("canUseTicketTemplates"),
        showTimeTrackingMenu: isFeatureEnabled("showTimeTrackingMenu"),
        canViewAllTimeEntries: isFeatureEnabled("canViewAllTimeEntries"),
        canCreateTimeEntries: isFeatureEnabled("canCreateTimeEntries"),
        canViewReports: isFeatureEnabled("canViewReports"),
        showClientMenu: isFeatureEnabled("showClientMenu"),
        canViewClients: isFeatureEnabled("canViewClients"),
        canCreateClients: isFeatureEnabled("canCreateClients"),
        showAdminMenu: isFeatureEnabled("showAdminMenu"),
        canViewUsers: isFeatureEnabled("canViewUsers"),
        canManageAutomation: isFeatureEnabled("canManageAutomation"),
        canManageData: isFeatureEnabled("canManageData"),
        // Task Management
        canViewTasks: isFeatureEnabled("canViewTasks"),
        canCreateTasks: isFeatureEnabled("canCreateTasks"),
        canEditTasks: isFeatureEnabled("canEditTasks"),
        canDeleteTasks: isFeatureEnabled("canDeleteTasks"),
        canAssignTasks: isFeatureEnabled("canAssignTasks"),
        canChangeTaskStatus: isFeatureEnabled("canChangeTaskStatus"),
        canChangeTaskPriority: isFeatureEnabled("canChangeTaskPriority"),
        canViewAllTasks: isFeatureEnabled("canViewAllTasks"),
        canViewMyTasks: isFeatureEnabled("canViewMyTasks"),
        canExportTasks: isFeatureEnabled("canExportTasks"),
      },
    };
    return buildNavigationSections(config);
  }, [isFeatureEnabled, roleCheck.isAdmin, roleCheck.roleName]);

  const {
    visibleItems,
    hiddenItems,
    allItems,
    saveCustomization,
    resetCustomization,
    toggleItemVisibility,
    moveItemUp,
    moveItemDown,
    moveChildUp,
    moveChildDown,
    toggleChildVisibility,
    isLoaded,
  } = useNavigationCustomization(defaultSections);

  return {
    isFeatureEnabled,
    canAccessCRM,
    roleCheck,
    visibleItems,
    hiddenItems,
    allItems,
    saveCustomization,
    resetCustomization,
    toggleItemVisibility,
    moveItemUp,
    moveItemDown,
    moveChildUp,
    moveChildDown,
    toggleChildVisibility,
    isLoaded,
  };
}
